// src/components/TiKaneSenpMinorModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientMinorService from '../services/clientMinorService';

const PAGE_SIZE = 20;
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export default function TiKaneSenpMinorModal({
  show,
  onHide,
  onSave,
  initialData = null,
}) {
  const init = initialData ?? {};
  const isEdit = !!init.id;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      minorId: init.minorId ?? null,
      status: init.status ?? 'ACTIVE',
    },
  });

  const [minors, setMinors] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;

    setError('');
    reset({
      minorId: init.minorId ?? null,
      status: init.status ?? 'ACTIVE',
    });

    clientMinorService
      .listAll()
      .then(setMinors)
      .catch(() => setMinors([]));
  }, [show, init.id, init.minorId, init.status, reset]);

  const toMinorOpt = (m) => ({
    value: m.id,
    label: `${m.firstName || ''} ${m.lastName || ''} (mineur)`.trim(),
  });

  const selectedMinorOption = useMemo(() => {
    const id = watch('minorId');
    if (!id) return null;
    const m = minors.find((x) => x.id === id);
    return m ? toMinorOpt(m) : { value: id, label: `Mineur #${id}` };
  }, [minors, watch('minorId')]);

  const loadMinorOptions = async (inputValue, _loaded, { page }) => {
    const q = normalize(inputValue);
    const filtered = minors.filter((m) =>
      normalize(
        `${m.firstName || ''} ${m.lastName || ''} ${m.parent?.firstName || ''} ${m.parent?.lastName || ''}`
      ).includes(q)
    );
    const start = (page - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE).map(toMinorOpt);
    const hasMore = start + PAGE_SIZE < filtered.length;

    return {
      options: slice,
      hasMore,
      additional: { page: page + 1 },
    };
  };

  const onSubmit = (data) => {
    if (!data.minorId) {
      setError('Veuillez sélectionner un mineur.');
      return;
    }
    setError('');
    onSave({
      id: init.id,
      minorId: data.minorId,
      status: data.status,
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit
              ? 'Modifier un compte Ti Kanè Senp (mineur)'
              : 'Nouveau compte Ti Kanè Senp (mineur)'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="g-3">
            <Col md={8}>
              <Form.Group controlId="minorId">
                <Form.Label>Mineur *</Form.Label>
                <Controller
                  name="minorId"
                  control={control}
                  rules={{ required: 'Requis' }}
                  render={({ field }) => (
                    <AsyncPaginate
                      value={selectedMinorOption}
                      onChange={(opt) => field.onChange(opt?.value ?? null)}
                      loadOptions={loadMinorOptions}
                      additional={{ page: 1 }}
                      debounceTimeout={300}
                      placeholder="Rechercher un mineur…"
                      isClearable
                    />
                  )}
                />
                {errors.minorId && (
                  <Form.Text className="text-danger">
                    {errors.minorId.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="status">
                <Form.Label>Statut</Form.Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="CLOSED">CLOSED</option>
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button variant="primary" type="submit">
            {isEdit ? 'Enregistrer les modifications' : 'Créer le compte'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
